import * as THREE from "three";

export const rotateUVs = (geometry: THREE.BufferGeometry, rotations: Record<string, number>) => {
    const uvAttribute = geometry.attributes.uv;
    const positionAttribute = geometry.attributes.position;
    const uvOffset = new THREE.Vector2(Math.random(), Math.random());

    if (!uvAttribute || !positionAttribute) return;

    const uvArray = new Float32Array(uvAttribute.array);
    const isIndexed = geometry.index !== null;
    const indexAttribute = isIndexed ? geometry.index : null;

    const degreesToRadians = (deg: number) => (deg * Math.PI) / 180;

    const rotateUV = (u: number, v: number, angle: number) => {
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);
        const centeredU = u - 0.5;
        const centeredV = v - 0.5;
        return [
            centeredU * cosA - centeredV * sinA + 0.5,
            centeredU * sinA + centeredV * cosA + 0.5
        ];
    };

    const faceNormals = {
        front: new THREE.Vector3(0, 0, 1),
        back: new THREE.Vector3(0, 0, -1),
        left: new THREE.Vector3(-1, 0, 0),
        right: new THREE.Vector3(1, 0, 0),
        top: new THREE.Vector3(0, 1, 0),
        bottom: new THREE.Vector3(0, -1, 0),
    };

    const normal = new THREE.Vector3();
    const faceCount = isIndexed && indexAttribute ? indexAttribute.count / 3 : positionAttribute.count / 3;

    for (let i = 0; i < faceCount; i++) {
        const i1 = isIndexed && indexAttribute ? indexAttribute.getX(i * 3) : i * 3;
        const i2 = isIndexed && indexAttribute ? indexAttribute.getX(i * 3 + 1) : i * 3 + 1;
        const i3 = isIndexed && indexAttribute ? indexAttribute.getX(i * 3 + 2) : i * 3 + 2;

        const v1 = new THREE.Vector3().fromBufferAttribute(positionAttribute, i1);
        const v2 = new THREE.Vector3().fromBufferAttribute(positionAttribute, i2);
        const v3 = new THREE.Vector3().fromBufferAttribute(positionAttribute, i3);

        // Compute face normal
        const edge1 = new THREE.Vector3().subVectors(v2, v1);
        const edge2 = new THREE.Vector3().subVectors(v3, v1);
        normal.crossVectors(edge1, edge2).normalize();

        // Determine face type
        let faceKey = "front"; // Default
        let maxDot = -Infinity;
        for (const key in faceNormals) {
            const dot = normal.dot(faceNormals[key as keyof typeof faceNormals]);
            if (dot > maxDot) {
                maxDot = dot;
                faceKey = key;
            }
        }

        const angle = degreesToRadians(rotations[faceKey] || 0);

        // Rotate UVs
        for (let j = 0; j < 3; j++) {
            const uvIndex = isIndexed && indexAttribute ? indexAttribute.getX(i * 3 + j) : i * 3 + j;
            const [newU, newV] = rotateUV(uvArray[uvIndex * 2], uvArray[uvIndex * 2 + 1], angle);
            uvArray[uvIndex * 2] = newU;
            uvArray[uvIndex * 2 + 1] = newV;
        }
    }

    for (let i = 0; i < uvArray.length; i += 2) {
        uvArray[i] += uvOffset.x; // Offset U (X)
        uvArray[i + 1] += uvOffset.y; // Offset V (Y)
    }

    geometry.setAttribute("uv", new THREE.BufferAttribute(uvArray, 2));
    geometry.attributes.uv.needsUpdate = true;
};
